/**
 * The model policy: which models are allowed to do each job, as a release.
 *
 * A bar of policies across the top and the rules editor below it. Each chip
 * carries only the tags that apply — live, my account, was live (see
 * `policyTags`); one with no tag has never been live and can still be changed.
 * The loop is: copy a policy, change what it allows, use it on your own
 * account, make it live. Only the live policy is what customers run on, and
 * using another on your account cannot touch them.
 *
 * This replaced per-user profiles, which existed for exactly the same purpose
 * and did it worse: a profile was not what a customer's call ran on, so
 * testing one tested the wrong thing.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import BenchResultsTransfer from './BenchResultsTransfer';
import HouseRulesEditor from './HouseRulesEditor';
import { CopyPolicyButton, PastePolicy } from './PolicyTransfer';
import {
  barOrder,
  mineId,
  policyNickname,
  policyNumber,
  policySentence,
  policyTags,
  TAG_WORDS,
  type PolicyTag,
} from '../../pipeline/policyTags';
import { diffLines } from '../../pipeline/policyDiff';
import type { PipelineDiffRow, PipelineVersion, StageEnumOption } from '../../types';

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

const TAG_TONE: Record<PolicyTag, string> = {
  live: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  mine: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  was_live: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
};

export default function ModelPolicy() {
  const [versions, setVersions] = useState<PipelineVersion[]>([]);
  const [stableId, setStableId] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [stageEnums, setStageEnums] = useState<StageEnumOption[]>([]);
  const [diff, setDiff] = useState<PipelineDiffRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  // Renaming in place: a name is a label, not the mapping, so any policy can
  // be renamed — including the live one.
  const [renaming, setRenaming] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await api.listPipelineVersions();
      setVersions(list.items);
      setStableId(list.stable_id ?? null);
      setTestingId(list.testing_id ?? null);
      // Land on what you are testing, else the live policy, else the newest.
      setSelectedId((cur) =>
        cur && list.items.some((v) => v.id === cur)
          ? cur
          : (list.testing_id ?? list.stable_id ?? list.items[0]?.id ?? null),
      );
    } catch (e) {
      setError(errText(e, 'Could not load the policies'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api
      .getStageEnums()
      .then((r) => setStageEnums(r?.stages || []))
      .catch(() => setStageEnums([]));
  }, []);

  const selected = versions.find((v) => v.id === selectedId) || null;
  const isDraft = selected?.status === 'DRAFT';
  const isLive = selected?.status === 'STABLE';
  // What the admin's own calls run on: the one they picked, else the live one.
  const mine = mineId(testingId, stableId);
  const runsOn = versions.find((v) => v.id === mine) || null;
  const isMine = selected?.id === mine;
  const liveVersion = versions.find((v) => v.id === stableId) || null;
  const stageLabel = (value: string) => stageEnums.find((s) => s.value === value)?.label ?? value;
  // Where a read-only view sends you to change something: the draft under test
  // if that is a draft, else the newest draft.
  const editDraft =
    (runsOn?.status === 'DRAFT' ? runsOn : null) ??
    versions.filter((v) => v.status === 'DRAFT').sort((a, b) => b.id - a.id)[0] ??
    null;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setDiff(null);
      await load();
    } catch (e) {
      setError(errText(e, 'That did not work'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="model-policy">
      <div className="mb-4 border rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mr-1">
            Policy:
          </span>
          {loading && <span className="text-sm text-gray-500">Loading…</span>}
          {barOrder(versions).map((v) => (
            <button
              key={v.id}
              onClick={() => {
                setSelectedId(v.id);
                setDiff(null);
                setRenaming(null);
              }}
              data-testid={`policy-${v.id}`}
              className={`px-3 py-1.5 rounded text-sm font-medium border transition-colors flex items-center gap-2 ${
                selectedId === v.id
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-indigo-400'
              }`}
            >
              <span className="flex flex-col items-start leading-tight">
                <span>{policyNumber(v)}</span>
                {policyNickname(v) && (
                  <span className={`text-[10px] font-normal ${selectedId === v.id ? 'text-indigo-100' : 'text-gray-400'}`}>
                    {policyNickname(v)}
                  </span>
                )}
              </span>
              {policyTags(v, mine).map((t) => (
                <span
                  key={t}
                  data-testid={`policy-${v.id}-tag-${t}`}
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TAG_TONE[t]}`}
                >
                  {TAG_WORDS[t]}
                </span>
              ))}
              {/* An empty LIVE policy is an outage for every customer; an empty
                  draft is merely unfinished. Say which. */}
              {v.stage_count === 0 && (
                <span
                  data-testid={`policy-${v.id}-empty`}
                  title={
                    v.status === 'STABLE'
                      ? 'This policy allows no models, so every customer call fails. Copy a policy that has rules and make it live.'
                      : 'No rules yet.'
                  }
                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    v.status === 'STABLE'
                      ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  EMPTY
                </span>
              )}
            </button>
          ))}
          {/* From another server: a policy copied there as text becomes a
              draft here, after showing whether this pool runs the same models. */}
          <PastePolicy
            onPasted={(id) =>
              void run(async () => {
                setSelectedId(id);
              })
            }
          />
        </div>

        {selected && (
          <div className="px-3 py-2 bg-white dark:bg-gray-800" data-testid="policy-actions">
            {/* The sentence on its own line, so the buttons get the full width:
                sharing the row pushed Delete — the one destructive action — onto
                a line of its own, floated right under everything else. */}
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-2">{policySentence(selected)}</p>
            <div className="flex items-center gap-2 flex-wrap">
              {/* ON THE LIVE ONE, "use it" means going back to what everyone
                  runs, so it clears your pick rather than pinning the live
                  policy — pinned, a later "make live" elsewhere would leave
                  your account behind on the old one. */}
              {!isMine && (
                <button
                  onClick={() =>
                    run(() => (isLive ? api.stopTestingPipelineVersion() : api.testPipelineVersion(selected.id)))
                  }
                  disabled={busy}
                  data-testid="use-on-my-account"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Use on my account
                </button>
              )}
              {isMine && !isLive && (
                <button
                  onClick={() => run(() => api.stopTestingPipelineVersion())}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Go back to live on my account
                </button>
              )}
              {!isLive && (
                <button
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Make ${policyNumber(selected)} live? Every customer switches to it immediately. The current live policy is kept, and you can make it live again to switch back.`,
                      )
                    )
                      return;
                    void run(() => api.promotePipelineVersion(selected.id));
                  }}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Make live
                </button>
              )}
              {/* What this version IS, then what you can do to it. */}
              <span className="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1" aria-hidden />
              {/* Nothing to compare the live one with. */}
              {!isLive && (
                <button
                  onClick={async () => {
                    setError(null);
                    if (diff) {
                      setDiff(null);
                      return;
                    }
                    try {
                      setDiff(await api.diffPipelineVersion(selected.id));
                    } catch (e) {
                      setError(errText(e, 'Could not compare'));
                    }
                  }}
                  disabled={busy}
                  data-testid="compare-with-live"
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  {diff ? 'Hide comparison' : 'Compare with live'}
                </button>
              )}
              {/* COPIES WHAT YOU ARE LOOKING AT. In the version bar it always
                  copied the live policy, whichever version was selected — so
                  "fork this one to change it" meant picking the right chip
                  afterwards and diffing to see what you actually got. */}
              <button
                onClick={() =>
                  run(async () => {
                    const d = await api.createPipelineDraft({ from_version_id: selected.id });
                    setSelectedId(d.id);
                  })
                }
                disabled={busy}
                data-testid="copy-to-draft"
                title={`A new policy you can change, copied from ${policyNumber(selected)}`}
                className="px-3 py-1.5 text-sm border border-dashed border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
              >
                Copy to a new policy
              </button>
              <CopyPolicyButton version={selected} />
              {renaming === null ? (
                <button
                  onClick={() => setRenaming(selected.name)}
                  disabled={busy}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Rename
                </button>
              ) : (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const name = renaming.trim();
                    if (!name || name === selected.name) {
                      setRenaming(null);
                      return;
                    }
                    void run(async () => {
                      await api.updatePipelineVersion(selected.id, { name });
                      setRenaming(null);
                    });
                  }}
                >
                  <input
                    autoFocus
                    value={renaming}
                    onChange={(e) => setRenaming(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setRenaming(null);
                    }}
                    placeholder="Policy name"
                    className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded"
                  />
                  <button type="submit" disabled={busy} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded">
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setRenaming(null)}
                    className="px-2 py-1.5 text-sm text-gray-500 hover:underline"
                  >
                    Cancel
                  </button>
                </form>
              )}
              {/* The live policy is what customers are running, so it is never
                  deletable. A retired one is: it is the rollback path only for
                  as long as you want it, and the server refuses if calls were
                  billed against it. */}
              {/* Not the one your own calls run on: the server refuses that
                  too, and hiding it says why before you try. */}
              {!isLive && !isMine && (
                <button
                  onClick={() => {
                    if (
                      !window.confirm(
                        isDraft
                          ? `Delete ${policyNumber(selected)}?`
                          : `Delete ${policyNumber(selected)}? It was live before, so you could no longer switch back to it.`,
                      )
                    )
                      return;
                    void run(async () => {
                      await api.deletePipelineVersion(selected.id);
                      setSelectedId(null);
                    });
                  }}
                  disabled={busy}
                  className="ml-auto px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                >
                  Delete
                </button>
              )}
            </div>
            {diff && (
              <div className="mt-3 text-xs" data-testid="policy-diff">
                {diff.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400">
                    {policyNumber(selected)} allows the same models as the live policy
                    {liveVersion ? ` (${policyNumber(liveVersion)})` : ''} for every job.
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-gray-500 dark:text-gray-400">
                      Jobs where {policyNumber(selected)} allows different models from the live policy
                      {liveVersion ? ` (${policyNumber(liveVersion)})` : ''}. Every other job is the same.
                    </p>
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                          <th className="py-1 pr-3 font-medium">Job</th>
                          <th className="py-1 pr-3 font-medium">Live{liveVersion ? ` (${policyNumber(liveVersion)})` : ''} allows</th>
                          <th className="py-1 pr-3 font-medium">{policyNumber(selected)} allows</th>
                          <th className="py-1 font-medium">What changes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffLines(diff, { label: stageLabel, here: policyNumber(selected) }).map((d) => (
                          <tr key={d.key} className="border-t border-gray-100 dark:border-gray-700 align-top text-gray-700 dark:text-gray-200">
                            <td className="py-1.5 pr-3 font-medium">{d.job}</td>
                            <td className="py-1.5 pr-3 font-mono">
                              {d.live.length ? (
                                d.live.map((m) => (
                                  <div key={m} className={d.lost.includes(m) ? 'text-red-700 dark:text-red-300' : ''}>
                                    {d.lost.includes(m) ? '− ' : ''}
                                    {m}
                                  </div>
                                ))
                              ) : (
                                <span className="font-sans italic text-gray-400">no rule of its own</span>
                              )}
                            </td>
                            <td className="py-1.5 pr-3 font-mono">
                              {d.here.length ? (
                                d.here.map((m) => (
                                  <div key={m} className={d.gained.includes(m) ? 'text-green-700 dark:text-green-300' : ''}>
                                    {d.gained.includes(m) ? '+ ' : ''}
                                    {m}
                                  </div>
                                ))
                              ) : (
                                <span className="font-sans italic text-gray-400">no rule of its own</span>
                              )}
                            </td>
                            <td className="py-1.5 text-gray-500 dark:text-gray-400">{d.sentence}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {selected && (
        <HouseRulesEditor
          key={selected.id}
          versionId={selected.id}
          versionName={policyNumber(selected)}
          readOnly={!isDraft}
          stageEnums={stageEnums}
          onChanged={load}
          runsOn={runsOn && { ...runsOn, name: policyNumber(runsOn) }}
          draft={editDraft && { ...editDraft, name: policyNumber(editDraft) }}
          onForkToDraft={async (fromVersionId) => {
            const d = await api.createPipelineDraft({ from_version_id: fromVersionId });
            await load();
            return d.id;
          }}
          onOpenVersion={(vid) => {
            setSelectedId(vid);
            setDiff(null);
            setRenaming(null);
          }}
        />
      )}
      {!loading && !selected && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          {/* Nothing is selected, so "Copy to a draft" is not on screen: this is
              the only way to a first policy. */}
          No policy yet.{' '}
          <button
            onClick={() =>
              run(async () => {
                const d = await api.createPipelineDraft({});
                setSelectedId(d.id);
              })
            }
            disabled={busy}
            className="underline text-indigo-700 dark:text-indigo-300"
          >
            Start a policy
          </button>
        </div>
      )}

      <BenchResultsTransfer />

    </div>
  );
}
